import { useState, useEffect, useContext } from "react";
import axios from "axios";
import Table from "rc-table";
import "../../styling/TableDashboard.css";
import { PiCaretUpDownFill } from "react-icons/pi";
import { FaSearch } from "react-icons/fa";
import { EventDataContext } from "../EventDataContext";

const TableDashboard = ({onEventClick, setInitialBusinessEventTab}) => {
  const [sortOrder, setSortOrder] = useState({});
  const [selectedRows, setSelectedRows] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const { EventData, setEventData } = useContext(EventDataContext);

  // Add currentPage state at the top of the component
const [currentPage, setCurrentPage] = useState(1);
const pageSize = 8; // Set to 8 entries per page

const filteredInvoices = invoices.filter((invoice) => {
  const clientName = invoice.clientName?.toLowerCase() || "";
  const invoiceNumber = invoice.invoiceNumber?.toLowerCase() || "";
  const search = searchTerm.toLowerCase();
  return clientName.includes(search) || invoiceNumber.includes(search);
});


// Calculate the total number of pages based on filtered results
const totalPages = Math.ceil(filteredInvoices.length / pageSize);

// Slice the filtered results to show only 8 items for the current page
const paginatedInvoices = filteredInvoices.slice(
  (currentPage - 1) * pageSize,
  currentPage * pageSize
);

// Optional: Reset to page 1 if search term changes
useEffect(() => {
  setCurrentPage(1);
}, [searchTerm]);

  // Fetch invoice data from API
  useEffect(() => {
    const fetchInvoices = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/invoices`,
          {
            withCredentials: true,
          }
        );

        const formatted = res.data.invoices.map((inv) => ({
          eventId: inv.eventId,
          clientName: inv.clientName,
          downloadUrl: inv.downloadUrl,
          invoiceNumber: inv.invoice.invoiceNumber,
          issueDate: inv.invoice.issuedDate,
          dueDate: inv.invoice.dueDate,
          status: inv.invoice.status,
          total: inv.invoice.total,
          billedTo: inv.invoice.billedTo,
        }));

        setInvoices(formatted);
      } catch (err) {
        console.error("Failed to fetch invoices", err);
      }
    };

    fetchInvoices();
  }, []);


  const fetchEventById = async (eventId) => {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/event/${eventId}`,
      { withCredentials: true }
    );
    return response.data.data.event; 
  } catch (error) {
    console.error("Failed to fetch event:", error);
    return null;
  }
};

  const columns = [
    {
      title: "",
      dataIndex: "id",
      key: "id",
      render: (_, record) => (
        <input
          type="checkbox"
          checked={selectedRows.includes(record.eventId)}
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedRows([...selectedRows, record.eventId]);
            } else {
              setSelectedRows(
                selectedRows.filter((id) => id !== record.eventId)
              );
            }
          }}
        />
      ),
      width: 9,
      align: "center",
    },
    {
      title: (
        <div className="custom-header" onClick={() => handleSort("clientName")}>
          <PiCaretUpDownFill className="table-header-icon" />
          Client
        </div>
      ),
      dataIndex: "clientName",
      key: "clientName",
      width: 100,
      align: "left",
      render: (_, record) => (
        <span style={{ color: "#1C1154" }}>
  <a
    href="#"
    onClick={async (e) => {
      e.preventDefault();

      const eventData = await fetchEventById(record.eventId);
      if (eventData) {
        setEventData(eventData);
        onEventClick(record.eventId); 
        setInitialBusinessEventTab(1); 
      } else {
        // handle error case if needed
      }
    }}

    style={{ color: "#080700", textDecoration: "none", cursor: "pointer", fontWeight: "500", fontStyle: 'bold', fontSize: '15px' }}

  >
    {record.clientName || record.billedTo?.name || "Unknown Client"}
  </a>
</span>

      ),
    },
    {
      title: (
        <div className="custom-header" onClick={() => handleSort("issueDate")}>
          <PiCaretUpDownFill className="table-header-icon" />
          Issued
        </div>
      ),
      dataIndex: "issueDate",
      key: "issueDate",
      width: 100,
      align: "left",
      render: (date) =>
        date
          ? new Date(date).toLocaleDateString("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
            })
          : "N/A",
    },
    {
      title: (
        <div className="custom-header" onClick={() => handleSort("dueDate")}>
          <PiCaretUpDownFill className="table-header-icon" />
          Due
        </div>
      ),
      dataIndex: "dueDate",
      key: "dueDate",
      width: 100,
      align: "left",
      render: (date) =>
        date
          ? new Date(date).toLocaleDateString("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
            })
          : "N/A",
    },
    {
  title: (
    <div className="custom-header" onClick={() => handleSort("status")}>
      <PiCaretUpDownFill className="table-header-icon" />
      Status
    </div>
  ),
  dataIndex: "status",
  key: "status",
  width: 100,
  align: "left",
  render: (status) => (
    <span
      style={{
        backgroundColor: status?.toLowerCase() === "paid" ? "#E1FAE3" : "transparent",
        color: status?.toLowerCase() === "paid" ? "#21A62C" : "#000000",
        padding: "4px 8px",
        borderRadius: "4px",
        display: "inline-block",
      }}
    >
      {status || "N/A"}
    </span>
  ),
},

    {
      title: (
        <div className="custom-header" onClick={() => handleSort("total")}>
          <PiCaretUpDownFill className="table-header-icon" />
          Payment Amount
        </div>
      ),
      dataIndex: "total",
      key: "total",
      width: 100,
      align: "left",
      render: (amount) => `$${amount?.toFixed(2) || "0.00"}`,
    },
  ];

  const handleSort = (column) => {
    setSortOrder((prevSortOrder) => ({
      ...prevSortOrder,
      [column]: prevSortOrder[column] === "asc" ? "desc" : "asc",
    }));

    const sortedInvoices = [...invoices].sort((a, b) => {
      const valA = a[column]?.toString().toLowerCase?.() || "";
      const valB = b[column]?.toString().toLowerCase?.() || "";
      return sortOrder[column] === "asc"
        ? valA.localeCompare(valB)
        : valB.localeCompare(valA);
    });

    setInvoices(sortedInvoices);
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedRows(invoices.map((item) => item.eventId));
    } else {
      setSelectedRows([]);
    }
  };

const handleDownload = async () => {
  const selectedInvoiceIds = invoices
    .filter((inv) => selectedRows.includes(inv.eventId))
    .map((inv) => ({ id: inv.eventId, clientName: inv.clientName }));

  for (let i = 0; i < selectedInvoiceIds.length; i++) {
    const { id, clientName } = selectedInvoiceIds[i];

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/event/invoice/${id}`,
        { withCredentials: true }
      );

      const url = response.data.downloadUrl;

      const link = document.createElement("a");
      link.href = url;
      link.download = `${clientName || "Invoice"}-${i + 1}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Wait 600ms before triggering the next download
      await new Promise((resolve) => setTimeout(resolve, 600));

    } catch (error) {
      console.error(`Failed to download invoice ${id}:`, error);
    }
  }
};

  const rowClassName = () => "custom-table-row";

  const customFooter = () => (
    <div
      style={{ 
        paddingBottom: "10px",
        marginLeft: "14px", 
        marginRight: "14px", // Added right margin for symmetry
        display: "flex", 
        alignItems: "center", // Changed to center for better button alignment
        justifyContent: "space-between" // Pushes pagination to the right
      }}
    >
      {/* Left Side: Checkbox and Download */}
      <div style={{ display: "flex", alignItems: "baseline" }}>
        <input
          type="checkbox"
          onChange={(e) => handleSelectAll(e.target.checked)}
          checked={
            selectedRows.length > 0 && selectedRows.length === invoices.length
          }
        />
        <span style={{ margin: "0 4px" }} />
        <button className="tableDownloadButton" style = {{width: '100%'}} onClick={() => handleDownload()}>
          Download
        </button>
      </div>
  
      {/* Right Side: Pagination Controls */}
      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <span style={{ fontSize: "14px", color: "#4D4D4D" }}>
          {/* Page {currentPage} of {totalPages || 1} */}
        </span>
        <button
          onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
          disabled={currentPage === 1}
          style={{
            cursor: currentPage === 1 ? "not-allowed" : "pointer",
            border: "none",
            background: "white",
            padding: "2px 8px",
            color: "#1c1156",
            fontSize: '25px',
            textDecoration: 'none', // Initial state
            transition: 'none'
          }}
        >
          {"<"}
        </button>
        <button style={{
                        background: '#fff', 
                        color: '#4D4D4D', 
                        border: '1px solid #D9D9D9', 
                        fontSize: '20px', 
                        width: '35px',
                        textDecoration: 'none', // Initial state
                        transition: 'none !important' // Ensures no flash of background
      
                      }}>
           {currentPage} 
        </button>
        <button
          onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
          disabled={currentPage === totalPages || totalPages === 0}
          style={{
            cursor: currentPage !== 1 ? "not-allowed" : "pointer",
            border: "none",
            background: "white",
            borderRadius: "0px",
            padding: "2px 8px",
            color: "#1c1156",
            fontSize: '25px'
          }}
        >
          {">"}
        </button>
      </div>
    </div>
  );

  const customRowClassName = (record, index) => {
    return "table-row-with-border";
  };

  
  return (
    <div>
      <div className="customDashboardTable">
        <div
          style={{
            paddingTop: "25px",
            paddingLeft: "15px",
            display: "flex",
            justifyContent: "left",
            width: "37.5%",
            alignItems: "baseline",
          }}
        >
          <h2
            style={{
              fontSize: "20px",
              fontWeight: "500",
              color: "#000000",
              marginTop: "0px",
              paddingRight: "19px",
            }}
          >
            Invoices
          </h2>
          <div style={{ color: "#4D4D4D" }}>
            {"Total: "}
            {filteredInvoices.length}
          </div>
        </div>

        <div
          style={{
            position: "relative",
            width: "81%",
            marginTop: "13px",
            marginLeft: "12px",
          }}
        >
          <div
            style={{
              position: "absolute",
              left: "10px",
              top: "13px",
              color: "gray",
            }}
          >
            <FaSearch />
          </div>
          <input
            type="text"
            style={{ padding: "10px", paddingLeft: "40px", width: "100%" }}
            placeholder="Search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {filteredInvoices.length === 0 ? (
          <div style={{ padding: "20px", textAlign: "center" }}>
            No paid invoices found
          </div>
        ) : (
          <Table
            columns={columns}
            data={paginatedInvoices}
            scroll={{ x: "100%" }}
            rowClassName={(record, index) =>
              `${rowClassName(record, index)} ${customRowClassName(
                record,
                index
              )}`
            }
            components={{
              header: {
                cell: (props) => (
                  <th
                    {...props}
                    style={{
                      borderBottom: "1px solid #D9D9D9",
                      height: "50px",
                      textAlign: "left",
                    }}
                  >
                    {props.children}
                  </th>
                ),
              },
            }}
            footer={customFooter}
          />
        )}
      </div>
    </div>
  );
};

export default TableDashboard;